import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, t } from '@sm/intl';
import { Container, Button } from '@sm/wds-react';
import { Typography } from '@wds/typography';
import { generateMetricsAttribute } from '@sm/metrics';
import { createURL } from '@sm/utils';
import { LogoWithText, TabletScreen, MobileScreen, DesktopScreen } from '@sm/webassets';

import useStyles from './useStyles';

const COPY = defineMessages({
  HEADER: {
    id: 'CovidEndpageHero.Header',
    defaultMessage: 'Help those who are helping others',
    description:
      '[Type: Header][vis: high] - The only message on the webpage. Used on a Nonbranded webpage to say thank you to survey respondents and signal the end of the survey.',
  },
  SUBHEADER: {
    id: 'CovidEndpageHero.Subheader',
    defaultMessage:
      'Take a survey to raise $0.50 for organizations like the American Red Cross, International Rescue Committee, Doctors Without Borders, and more.',
    description:
      '[Type: Label][Vis: med] - This figure of speech is used to introduce and display the SurveyMonkey Logo',
  },
  HEADER_CTA: {
    id: 'CovidEndpageHero.HeaderCTA',
    defaultMessage: 'Take a survey',
    description:
      '[Type: Label][Vis: med] - This is the phrase used inside a button that directs views to a signup page',
  },
});

const CovidEndpageHero = ({ utSource, utSource2 }) => {
  const classes = useStyles();
  const contributeURL = createURL('https://contribute.surveymonkey.com/sign-up/create-account', {
    ut_source: utSource,
    ut_source2: utSource2,
  });

  return (
    <>
      <a alt="SurveyMonkey Logo" data-testid="CovidEndpageHero__Logo" href={createURL('/', {})}>
        <LogoWithText className={classes.logo} />
      </a>
      <DesktopScreen>
        <Container flex flexJustify="center" className={classes.wideBackgroundContainer}>
          <Container flex flexAlign="center" flexDirection="row" className={classes.heroContainer}>
            <Container className={classes.redHeartGift} ml={3} mt={8} />
            <Container className={classes.heroCopy}>
              <Typography color="dark" component="div" mt={6} mb={6} variant="hero1">
                <span className={classes.heroHeader}>{t(COPY.HEADER)}</span>
              </Typography>
              <Typography color="dark" component="div" mb={6} variant="body">
                <span className={classes.heroSubHeader}>{t(COPY.SUBHEADER)}</span>
              </Typography>
              <Button
                size="lg"
                href={contributeURL}
                data-sm-metric={generateMetricsAttribute({
                  data: {
                    actionType: `${utSource}_click_${utSource2}_cta_to_sign_up`,
                    actionFlow: { utSource },
                  },
                })}
              >
                {t(COPY.HEADER_CTA)}
              </Button>
            </Container>
          </Container>
        </Container>
      </DesktopScreen>
      <TabletScreen>
        <Container flex flexJustify="center" flexDirection="column" className={classes.slimBackgroundContainer}>
          <Container
            flex
            flexJustify="center"
            flexDirection="column"
            mt={8}
            mb={7}
            className={[classes.heroCopy, classes.heroContainer]}
          >
            <Typography color="dark" component="div" mb={6} variant="hero1" align="center">
              <span className={classes.heroHeader}>{t(COPY.HEADER)}</span>
            </Typography>
            <Typography color="dark" component="div" mb={6} variant="body" align="center">
              <span className={classes.heroSubHeader}>{t(COPY.SUBHEADER)}</span>
            </Typography>
            <Button
              size="lg"
              href={contributeURL}
              data-sm-metric={generateMetricsAttribute({
                data: {
                  actionType: `${utSource}_click_${utSource2}_cta_to_sign_up`,
                  actionFlow: { utSource },
                },
              })}
            >
              {t(COPY.HEADER_CTA)}
            </Button>
          </Container>
          <Container className={classes.redHeartGift} mt={9} />
        </Container>
      </TabletScreen>
      <MobileScreen>
        <Container flex flexJustify="center" flexDirection="column" className={classes.slimBackgroundContainer}>
          <Container
            flex
            flexJustify="center"
            flexDirection="column"
            mt={8}
            mb={7}
            className={[classes.heroCopy, classes.heroContainer]}
          >
            <Typography color="dark" component="div" mb={6} variant="hero1" align="center">
              <span className={classes.heroHeader}>{t(COPY.HEADER)}</span>
            </Typography>
            <Typography color="dark" component="div" mb={6} variant="body" align="center">
              <span className={classes.heroSubHeader}>{t(COPY.SUBHEADER)}</span>
            </Typography>
            <Button
              size="lg"
              href={contributeURL}
              data-sm-metric={generateMetricsAttribute({
                data: {
                  actionType: `${utSource}_click_${utSource2}_cta_to_sign_up`,
                  actionFlow: { utSource },
                },
              })}
            >
              {t(COPY.HEADER_CTA)}
            </Button>
          </Container>
          <Container className={classes.redHeartGift} />
        </Container>
      </MobileScreen>
    </>
  );
};

CovidEndpageHero.propTypes = {
  utSource: PropTypes.string,
  utSource2: PropTypes.string,
};

CovidEndpageHero.defaultProps = {
  utSource: 'survey_thanks',
  utSource2: 'covid_endpage',
};

export default CovidEndpageHero;
